import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Landmark, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_BADGE = {
  past_due: 'bg-amber-900 text-amber-200',
  read_only: 'bg-red-900 text-red-200',
  lapsed: 'bg-gray-700 text-gray-300',
};

const STATE_LABEL = {
  past_due: 'Past due',
  read_only: 'Read-only',
  lapsed: 'Lapsed',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/**
 * Read-only admin panel (Round 2) — surfaces users in past_due / read_only / lapsed
 * state from /api/admin/failed-renewals. No actions in Round 2.
 */
const FailedRenewalsPanel = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const t = localStorage.getItem('auth_token') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/admin/failed-renewals`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.rows || []);
      setCount(d.count || 0);
    } catch (e) {
      setError(e.message || 'Failed to load failed renewals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid="failed-renewals-panel">
      <div className="p-3 sm:p-6 border-b border-gray-700">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-900/40 rounded-lg flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">Failed Renewals</h3>
              <p className="text-sm text-gray-400 mt-1">
                Users currently in past-due, read-only, or lapsed state. Read-only view.
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition flex items-center space-x-2"
            data-testid="failed-renewals-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {error && (
          <div className="p-6 text-sm text-red-300" data-testid="failed-renewals-error">{error}</div>
        )}
        {!error && loading && rows.length === 0 && (
          <div className="text-center py-12 text-gray-400"><RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />Loading…</div>
        )}
        {!error && !loading && count === 0 && (
          <div className="text-center py-12 text-gray-400" data-testid="failed-renewals-empty">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No failed renewals. Healthy.</p>
          </div>
        )}
        {!error && count > 0 && (
          <table className="w-full" data-testid="failed-renewals-table">
            <thead className="bg-gray-900">
              <tr>
                {['User', 'Country', 'State', 'Days', 'Retries', 'Next/Until', 'Method'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {rows.map((u) => (
                <tr key={u.user_id} className="hover:bg-gray-750" data-testid={`failed-renewals-row-${u.user_id}`}>
                  <td className="px-6 py-4">
                    <div className="text-sm text-white font-medium">{u.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-400">{u.email}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">{u.country || '—'}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATE_BADGE[u.subscription_state] || 'bg-gray-700 text-gray-300'}`}>
                      {STATE_LABEL[u.subscription_state] || u.subscription_state}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">{u.days_in_state ?? '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{u.renewal_retry_count || 0}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{fmtDate(u.subscriber_until)}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      {u.payment_provider === 'paystack' && <Landmark className="w-3.5 h-3.5 text-emerald-400" />}
                      {u.payment_provider === 'stripe' && <CreditCard className="w-3.5 h-3.5 text-purple-400" />}
                      <span className="text-xs">
                        {u.payment_provider || '—'}
                        {u.payment_bank ? ` · ${u.payment_bank}` : ''}
                        {u.payment_last4 ? ` · ⋯${u.payment_last4}` : ''}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {count > 0 && (
        <div className="p-4 border-t border-gray-700 bg-gray-900 text-sm text-gray-400">
          {count} user{count !== 1 ? 's' : ''} requiring attention
        </div>
      )}
    </div>
  );
};

export default FailedRenewalsPanel;
